import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  createJournalEntry,
  deleteJournalEntry,
  getCashFlowAccounts,
  getJournalEntries,
  updateJournalEntry,
} from '../../../../services/treasuryAPI';
import { isDateInRange, isDateInSelectedPeriod, parseDateSafe } from '../../../placeholders/periodSummaryUtils';
import AccountingJournalTable3 from './AccountingJournalTable3';

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const chartLeaf = (row) => {
  for (const key of [
    'detail_level_6',
    'detail_level_5',
    'detail_level_4',
    'detail_level_3',
    'detail_level_2',
    'detail_level_1',
  ]) {
    const value = String(row?.[key] || '').trim();
    if (value) return value;
  }
  return row?.account_code || 'Cuenta';
};

const emptyLine = (side) => ({
  side,
  chart_account_id: '',
  amount: '',
  description: '',
  line_order: side === 'debe' ? 10 : 20,
});

const emptyForm = () => ({
  entry_date: new Date().toISOString().slice(0, 10),
  description: '',
  reference_code: '',
  status: 'Borrador',
  lines: [emptyLine('debe'), emptyLine('haber')],
});

/**
 * Asientos + Libro diario (lista cronológica de asientos).
 * mode: 'asientos' | 'diario'
 */
const AccountingJournalPanel = ({
  ui,
  mode = 'asientos',
  isDarkMode = false,
  onBooksChanged,
  dateFrom = '',
  dateTo = '',
  selectedPeriod = null,
}) => {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState(mode === 'diario' ? 'Contabilizado' : 'all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [entries, chart] = await Promise.all([getJournalEntries(params), getCashFlowAccounts()]);
      setRows(Array.isArray(entries) ? entries : []);
      setAccounts(Array.isArray(chart) ? chart : []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los asientos.');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const accountOptions = useMemo(
    () =>
      accounts.map((row) => ({
        value: String(row.id),
        label: `${row.account_code || 'COA'} · ${chartLeaf(row)}`,
      })),
    [accounts]
  );

  const periodRange = useMemo(() => {
    if (!dateFrom && !dateTo) return null;
    const start = dateFrom ? parseDateSafe(dateFrom) : null;
    const end = dateTo ? parseDateSafe(dateTo) : null;
    if (!start && !end) return null;
    return {
      start: start || new Date(0),
      end: end || new Date(8640000000000000),
    };
  }, [dateFrom, dateTo]);

  const visibleRows = useMemo(() => {
    if (selectedPeriod) {
      return rows.filter((row) => isDateInSelectedPeriod(row.entry_date, selectedPeriod));
    }
    if (!periodRange) return rows;
    return rows.filter((row) => isDateInRange(row.entry_date, periodRange));
  }, [rows, periodRange, selectedPeriod]);

  const inputStyle = {
    height: 34,
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder}`,
    background: ui.inputBg,
    color: ui.inputText,
    padding: '0 10px',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
  };
  const primaryBtn = {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive,
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
  const ghostBtn = {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 8px',
    cursor: 'pointer',
    fontSize: 12,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      entry_date: String(row.entry_date || '').slice(0, 10),
      description: row.description || '',
      reference_code: row.reference_code || '',
      status: row.status || 'Borrador',
      lines: (row.lines || []).map((line) => ({
        side: line.side,
        chart_account_id: line.chart_account_id ? String(line.chart_account_id) : '',
        amount: String(line.amount ?? ''),
        description: line.description || '',
        line_order: line.line_order || 10,
      })),
    });
    setFormError('');
    setShowForm(true);
  };

  const updateLine = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, idx) => (idx === index ? { ...line, ...patch } : line)),
    }));
  };

  const addLine = (side) => {
    setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine(side)] }));
  };

  const removeLine = (index) => {
    setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, idx) => idx !== index) }));
  };

  const totals = useMemo(() => {
    const debe = form.lines
      .filter((l) => l.side === 'debe')
      .reduce((s, l) => s + Number(l.amount || 0), 0);
    const haber = form.lines
      .filter((l) => l.side === 'haber')
      .reduce((s, l) => s + Number(l.amount || 0), 0);
    return { debe, haber, diff: Math.abs(debe - haber) };
  }, [form.lines]);

  const handleSave = async () => {
    if (!form.entry_date) {
      setFormError('La fecha es obligatoria.');
      return;
    }
    const lines = form.lines.filter((l) => l.chart_account_id && Number(l.amount) > 0);
    if (lines.length < 2) {
      setFormError('Agrega al menos una línea DEBE y una HABER.');
      return;
    }
    const payload = {
      entry_date: form.entry_date,
      description: form.description,
      reference_code: form.reference_code,
      status: form.status,
      lines: lines.map((line, idx) => ({
        side: line.side,
        chart_account_id: Number(line.chart_account_id),
        amount: Number(line.amount),
        description: line.description || '',
        line_order: Number(line.line_order || (idx + 1) * 10),
      })),
    };
    try {
      setIsSaving(true);
      setFormError('');
      if (editingId) await updateJournalEntry(editingId, payload);
      else await createJournalEntry(payload);
      setShowForm(false);
      await load();
      onBooksChanged?.();
    } catch (err) {
      setFormError(err.message || 'No se pudo guardar el asiento.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteJournalEntry(row.id);
      await load();
      onBooksChanged?.();
    } catch (err) {
      window.alert(err.message || 'No se pudo anular.');
    }
  };

  const title = mode === 'diario' ? 'Libro diario' : 'Asientos contables';

  const renderSideEditor = (side) => {
    const sideLines = form.lines.map((line, index) => ({ line, index })).filter(({ line }) => line.side === side);
    return (
      <div
        style={{
          border: `1px solid ${side === 'debe' ? (isDarkMode ? '#1f4d99' : '#bfdbfe') : isDarkMode ? '#065f46' : '#a7f3d0'}`,
          borderRadius: 10,
          padding: 10,
          background: side === 'debe' ? (isDarkMode ? '#0b1d3b' : '#eff6ff') : isDarkMode ? '#052e26' : '#ecfdf5',
          display: 'grid',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: side === 'debe' ? '#3b82f6' : '#10b981' }}>
            {side === 'debe' ? 'DEBE' : 'HABER'}
          </p>
          <button type="button" style={ghostBtn} onClick={() => addLine(side)}>
            <Plus size={12} /> Línea
          </button>
        </div>
        {sideLines.map(({ line, index }) => (
          <div key={`${side}-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 32px', gap: 6 }}>
            <select
              value={line.chart_account_id}
              onChange={(e) => updateLine(index, { chart_account_id: e.target.value })}
              style={inputStyle}
            >
              <option value="">Cuenta</option>
              {accountOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={line.amount}
              onChange={(e) => updateLine(index, { amount: e.target.value })}
              style={inputStyle}
              placeholder="Monto"
            />
            <button
              type="button"
              style={{ ...ghostBtn, height: 34, color: ui.danger, borderColor: ui.danger }}
              onClick={() => removeLine(index)}
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <AccountingJournalTable3
        rows={visibleRows}
        loading={isLoading}
        error={error}
        isDarkMode={isDarkMode}
        mode={mode}
        tableTitle={title}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onAdd={mode === 'asientos' ? openCreate : undefined}
        onEdit={mode === 'asientos' ? openEdit : undefined}
        onDelete={mode === 'asientos' ? handleDelete : undefined}
      />

      {showForm ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: ui.overlay || 'rgba(0,0,0,0.45)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setShowForm(false)}
        >
          <div
            style={{
              width: 560,
              maxWidth: '100%',
              height: '100%',
              background: ui.panel,
              borderLeft: `1px solid ${ui.border}`,
              padding: 20,
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateRows: 'auto 1fr auto',
              gap: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>
                {editingId ? 'Editar asiento' : 'Nuevo asiento'}
              </p>
            </div>
            <div style={{ overflowY: 'auto', display: 'grid', gap: 10, alignContent: 'start' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Fecha
                  <input
                    type="date"
                    value={form.entry_date}
                    onChange={(e) => setForm((p) => ({ ...p, entry_date: e.target.value }))}
                    style={inputStyle}
                  />
                </label>
                <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                  Estado
                  <select
                    value={form.status}
                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="Borrador">Borrador</option>
                    <option value="Contabilizado">Contabilizado</option>
                  </select>
                </label>
              </div>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Descripción
                <input
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: 6, fontSize: 12, color: ui.textSecondary }}>
                Referencia
                <input
                  value={form.reference_code}
                  onChange={(e) => setForm((p) => ({ ...p, reference_code: e.target.value }))}
                  style={inputStyle}
                />
              </label>
              {renderSideEditor('debe')}
              {renderSideEditor('haber')}
              <p style={{ margin: 0, fontSize: 12, color: totals.diff > 0.009 ? ui.danger : ui.muted }}>
                DEBE {money(totals.debe)} · HABER {money(totals.haber)}
                {totals.diff > 0.009 ? ` · diferencia ${money(totals.diff)}` : ' · cuadrado'}
              </p>
              {formError ? <p style={{ margin: 0, color: ui.danger, fontSize: 12 }}>{formError}</p> : null}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={{ ...ghostBtn, height: 34 }} onClick={() => setShowForm(false)}>
                Cancelar
              </button>
              <button type="button" style={primaryBtn} onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Guardando...' : 'Guardar asiento'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AccountingJournalPanel;
